import { useControlledState } from '@react-stately/utils';
import * as React from 'react';

import { useLiveRef } from '@gitbook/util-react-hooks';
import { assertIsNotUndefined } from '@gitbook/util-ts';

interface UseCoverResizerOptions {
    /** A ref to the container element */
    containerRef?: React.RefObject<HTMLElement | null>;
    /** A ref to the resize handle element */
    resizeHandleRef?: React.RefObject<HTMLButtonElement | null>;
    /** The current height of the cover in pixels (controlled) */
    value?: number;
    /** The default height of the cover in pixels (uncontrolled) */
    defaultValue?: number;
    /** The minimum height of the cover in pixels */
    min: number;
    /** The maximum height of the cover in pixels */
    max: number;
    /** Whether the cover is read only */
    readOnly?: boolean;
    /** A callback to commit the final height of the cover */
    onChange?: (height: number) => void;
}

interface UseCoverResizerResult {
    /** The selected height of the cover in pixels */
    height: number;
    /** Whether the cover is being resized */
    resizing: boolean;
}

/**
 * Hook to handle the resizing of the cover, returning:
 * - `resizing`: whether the cover is being resized
 * - `height`: the height of the cover in pixels
 */
export function useCoverResizer(options: UseCoverResizerOptions): UseCoverResizerResult {
    const { min, max, readOnly, onChange } = options;
    const [height, setHeight] = useControlledState(
        options.value,
        options.defaultValue ?? 0,
        onChange
    );
    const [resizing, setResizing] = React.useState(false);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const dragOriginHeightRef = React.useRef(height);
    const dragStartYRef = React.useRef<number | null>(null);
    const currentHeightRef = useLiveRef(height);

    // Effect for mousedown on resize handle
    React.useEffect(() => {
        assertIsNotUndefined(options.resizeHandleRef);
        const handle = options.resizeHandleRef.current;
        if (!handle || readOnly) {
            return;
        }

        const handlePointerDown = (event: PointerEvent) => {
            if (event.button !== 0) {
                return;
            }
            dragOriginHeightRef.current = height;
            dragStartYRef.current = event.clientY;
            setResizing(true);
        };

        handle.addEventListener('pointerdown', handlePointerDown);
        return () => {
            handle.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [options.resizeHandleRef, readOnly, height]);

    // Effect for mousemove and mouseup during resize
    React.useEffect(() => {
        if (!resizing) {
            return;
        }

        const handleMouseMove = (event: MouseEvent) => {
            if (dragStartYRef.current === null) {
                return;
            }
            const deltaY = event.clientY - dragStartYRef.current;
            const computedHeight = dragOriginHeightRef.current + deltaY;
            const clamped = Math.min(max, Math.max(min, computedHeight));

            // If shift is held, snap to 10px increments
            const step = event.shiftKey ? 10 : 1;
            const rounded = Math.round(clamped / step) * step;

            // Update the height for the UI to update live (before committing)
            setHeight(rounded);
        };

        const handleMouseUp = () => {
            setResizing(false);
            onChange?.(currentHeightRef.current);
            dragStartYRef.current = null;
            // Blur the handle after mouse drag completes
            options.resizeHandleRef?.current?.blur();
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [resizing, options.resizeHandleRef, min, max, onChange]);

    // Effect for keydown on resize handle
    React.useEffect(() => {
        const handle = options.resizeHandleRef?.current;
        if (!handle || readOnly) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            const allowedKeys = ['ArrowUp', 'ArrowDown', 'Home', 'End'];
            if (!allowedKeys.includes(event.key)) {
                return;
            }

            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            // Set resizing to true to display the correct tooltip values and crosshair UI etc
            setResizing(true);

            const step = event.shiftKey ? 10 : 1; // Shift for larger steps
            let newHeight = height;

            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    newHeight = height - step;
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    newHeight = height + step;
                    break;
                case 'Home':
                    event.preventDefault();
                    newHeight = min;
                    break;
                case 'End':
                    event.preventDefault();
                    newHeight = max;
                    break;
                default:
                    return;
            }

            const clamped = Math.min(max, Math.max(min, newHeight));
            const rounded = Math.round(clamped);

            // Update DOM and state
            if (options.containerRef?.current) {
                options.containerRef.current.style.height = `${rounded}px`;
            }

            setHeight(rounded);
            onChange?.(rounded);

            // Set timeout to reset resizing state and store the ID
            const timeoutId = setTimeout(() => {
                setResizing(false);
                timeoutRef.current = null;
            }, 1000);
            timeoutRef.current = timeoutId;
        };

        handle.addEventListener('keydown', handleKeyDown);
        return () => {
            handle.removeEventListener('keydown', handleKeyDown);
            // Cleanup timeout on unmount
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [
        options.resizeHandleRef,
        options.containerRef,
        readOnly,
        min,
        max,
        height,
        onChange,
        setHeight,
    ]);

    return {
        resizing,
        height,
    };
}
